import { cn } from "@/lib/utils";

/**
 * Numbered wizard steps; `current` is 1-based. On phones the row keeps clear
 * of the dialog's absolutely-positioned close button and names only the
 * current step (the others stay readable to screen readers), so every step
 * fits. Wider screens keep the full row.
 */
export function WizardStepIndicator({
  labels,
  current,
  activeClassName = "bg-[#FFAF53] text-black",
  className,
  connectorClassName,
}: {
  labels: string[];
  current: number;
  activeClassName?: string;
  className?: string;
  connectorClassName?: string;
}) {
  return (
    <ol
      className={cn(
        "flex items-center gap-2 pb-2 pl-5 pr-12 pt-5 sm:gap-3 sm:pl-6 sm:pr-6 sm:pt-6",
        className,
      )}
    >
      {labels.map((label, i) => {
        const step = i + 1;
        const reached = current >= step;
        return (
          <li
            key={label}
            aria-current={step === current ? "step" : undefined}
            className="flex items-center gap-2 sm:gap-3"
          >
            {i > 0 && (
              <div
                aria-hidden
                className={cn("h-px w-4 bg-contrast/10 sm:w-8", connectorClassName)}
              />
            )}
            <div className="flex items-center gap-2 whitespace-nowrap">
              <div
                className={cn(
                  "flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-medium",
                  reached ? activeClassName : "bg-contrast/6 text-contrast/30",
                )}
              >
                {step}
              </div>
              <span
                className={cn(
                  "text-[13px] font-medium",
                  reached ? "text-heading" : "text-contrast/30",
                  step !== current && "max-sm:sr-only",
                )}
              >
                {label}
              </span>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
